import type {
  NewMarketsFilter,
  TrafficChangeDirection,
  TrafficChangesFilter,
  TrafficSignal,
} from '../../domain/signal';
import { CountryService } from '../country/country';
import { translate } from '../i18n';
import { formatWithSuffix } from '../numberFormat';

import { PERIOD_TO_NAME_MAP } from './signal';

const NEW_MARKETS_TAG = 'New market';
const GROWING_TAG = 'Growing';
const DECLINING_TAG = 'Declining';

export const trafficChangesDirectionToTagMap: Record<TrafficChangeDirection, string> = {
  grew: GROWING_TAG,
  declined: DECLINING_TAG,
};

const buildPeriodText = <F extends { readonly period: string }>(filters: F): string => {
  const { period } = filters;

  return `over the last ${PERIOD_TO_NAME_MAP[period]}`;
};

const buildTrafficMetricText = <F extends { readonly metric: string }>(filters: F): string => {
  const { metric } = filters;

  return translate(`company.signals.filters.traffic-metric.${metric}.name`);
};

const buildCountryText = <F extends { readonly country: number }>(filters: F): string => {
  const { country } = filters;

  const countryName = CountryService.getCountryName(country);

  return countryName ?? '';
};

const buildCountriesText = (countries: readonly number[]): string => {
  const numberOfCountries = countries.length;

  if (numberOfCountries === 1) {
    return `${numberOfCountries} country`;
  }

  return `${numberOfCountries} countries`;
};

const getTrafficChangesEventTitle = (
  trafficChanges: TrafficChangesFilter['trafficChanges'],
  domain: string
): string => {
  const actionText = trafficChanges.direction;

  const countryText = buildCountryText(trafficChanges);
  const channelName = buildTrafficMetricText(trafficChanges);
  const periodText = buildPeriodText(trafficChanges);
  const trafficChangesValue = trafficChanges.value * 100;

  return `${countryText} ${domain}'s ${channelName.toLowerCase()} ${actionText} ${periodText} by ${trafficChangesValue}%+`.trim();
};

const getNewMarketEventTitle = (newMarkets: NewMarketsFilter['newMarkets']): string => {
  const trafficMetricText = buildTrafficMetricText(newMarkets);
  const thresholdValue = formatWithSuffix(newMarkets.thresholdValue);

  return `New market: ${trafficMetricText} crossed ${thresholdValue} in `;
};

export const isTrafficChangesModel = (object: unknown): object is TrafficChangesFilter['trafficChanges'] => {
  if (typeof object !== 'object' || object === null) {
    return false;
  }

  const trafficChanges = object as Partial<TrafficChangesFilter['trafficChanges']>;

  const conditions = [
    typeof trafficChanges.metric === 'string',
    typeof trafficChanges.period === 'string',
    typeof trafficChanges.direction === 'string',
    typeof trafficChanges.country === 'number',
    typeof trafficChanges.value === 'number',
  ];

  return conditions.every(Boolean);
};

export const isTrafficMarketModel = (object: unknown): object is NewMarketsFilter['newMarkets'] => {
  if (typeof object !== 'object' || object === null) {
    return false;
  }

  const trafficMarket = object as Partial<NewMarketsFilter['newMarkets']>;
  const countries = trafficMarket.countries;
  const shouldCheckCountriesType = Array.isArray(countries) && countries.length > 0;

  const conditions = [
    typeof trafficMarket.metric === 'string',
    typeof trafficMarket.thresholdValue === 'number',
    shouldCheckCountriesType && countries.every((item: unknown) => typeof item === 'number'),
  ];

  return conditions.every(Boolean);
};

export const getTrafficSignalEventConfig = (
  signal: TrafficSignal,
  domain: string
): { title: string; type?: 'trafficChanges' | 'newMarkets'; countriesText?: string } => {
  const { filters, countries } = signal;

  if ('trafficChanges' in filters && isTrafficChangesModel(filters.trafficChanges)) {
    return {
      title: getTrafficChangesEventTitle(filters.trafficChanges, domain),
      type: 'trafficChanges',
    };
  }

  if ('newMarkets' in filters && isTrafficMarketModel(filters.newMarkets)) {
    const countriesText = buildCountriesText(countries ?? []);
    return {
      title: getNewMarketEventTitle(filters.newMarkets),
      type: 'newMarkets',
      countriesText,
    };
  }

  return { title: '' };
};

export const getTrafficSignalEventTags = (signal: TrafficSignal): string[] => {
  const { filters } = signal;

  if ('trafficChanges' in filters && isTrafficChangesModel(filters.trafficChanges)) {
    const { trafficChanges } = filters;

    const tag = trafficChangesDirectionToTagMap[trafficChanges.direction];

    return tag ? [tag] : [];
  }

  if ('newMarkets' in filters && isTrafficMarketModel(filters.newMarkets)) {
    return [NEW_MARKETS_TAG];
  }

  return [];
};
